/**
 * Rejoice.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import FeeSplit from '../components/FeeSplit';
import { CardHeading, EmptyState, Notice, StatusPill } from '../components/ui';
import { api } from '../lib/api';
import { formatAmount, formatDate } from '../lib/format';
import { trackEvent } from '../lib/consent';

export default function Confirmation() {
  const { t } = useTranslation();
  const { referenceId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    api
      .order(referenceId, controller.signal)
      .then(({ order: record }) => setOrder(record))
      .catch((err) => err.name !== 'AbortError' && setError(err.message));
    return () => controller.abort();
  }, [referenceId]);

  // Fires once the confirmation actually renders — `place_offering` (Checkout)
  // only tells us the order was created, not that the donor reached this page.
  // Keyed on the reference so a re-fetch of the same order doesn't re-fire.
  useEffect(() => {
    if (!order) return;
    trackEvent('view_confirmation', {
      transaction_id: order.reference,
      value: (order.amountCents ?? 0) / 100,
      currency: 'USD',
      item_id: order.service?.id,
      status: order.status,
    });
  }, [order?.reference]);

  if (error) {
    return (
      <div className="shell section">
        <EmptyState
          icon="alert"
          title={t('confirmation.notFound', 'We cannot find that offering')}
          action={<Link to="/offerings" className="btn btn--primary">{t('confirmation.myOfferings', 'My offerings')}</Link>}
        >
          {error}
        </EmptyState>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="shell section">
        <div className="skeleton" style={{ height: 300, borderRadius: 'var(--radius-lg)' }} />
      </div>
    );
  }

  return (
    <div className="shell shell--narrow" style={{ paddingBlock: 'var(--space-3xl) var(--space-5xl)' }}>
      <div className="rejoice">
        <span className="seal">
          <Icon name="sparkles" size={48} />
        </span>

        {/* Preserving original Tibetan script intact */}
        <p className="tibetan tibetan--gold" style={{ fontSize: 26, lineHeight: 1.6 }} lang="bo">
          ཡི་རང་བ།
        </p>
        <h1 className="display display--lg">{t('confirmation.rejoiceTitle', 'Rejoice.')}</h1>
        <p className="lede" style={{ maxWidth: '46ch' }}>
          {t('confirmation.rejoiceBody', {
            name: order.monastery.name,
            defaultValue: `Your offering is entered for ${order.monastery.name}, and your dedication travels with it exactly as you wrote it.`,
          })}
        </p>

        <p className="rejoice__ref">{order.reference}</p>
        <StatusPill status={order.status} />
      </div>

      <div className="stack stack--lg">
        {order.state === 'pending_payment' ? (
          <Notice tone="warn" icon="info" title={t('confirmation.processingTitle', 'Confirming your payment')}>
            {t(
              'confirmation.processingBody',
              'Your dedication is saved. This page updates once the payment clears — it usually takes only a moment.'
            )}
          </Notice>
        ) : (
          <Notice tone="info" icon="lotus" title={t('confirmation.heldTitle', 'Your offering is held')}>
            {t(
              'confirmation.heldBody',
              'Your contribution has been received. The ritual is not yet placed with the monastery — we confirm with you before it begins, and it is fully refundable until then.'
            )}
          </Notice>
        )}

        <section className="card card__pad">
          <CardHeading icon="quill" title={t('confirmation.yourDedication', 'Your dedication')} />
          <div className="dedication-preview">
            <p className="dedication-preview__en">“{order.dedicationText}”</p>
            {order.dedicationTibetan && (
              <p className="dedication-preview__bo tibetan" lang="bo">
                {order.dedicationTibetan}
              </p>
            )}
            <p className="subtle">
              {t('confirmation.offeredUnder', { name: order.donorName })}
            </p>
          </div>
        </section>

        <section className="card card__pad">
          <CardHeading icon="receipt" title={t('confirmation.theRecord', 'The record')} />
          <dl>
            <div className="summary-line"><dt>{t('confirmation.reference', 'Reference')}</dt><dd>{order.reference}</dd></div>
            <div className="summary-line"><dt>{t('confirmation.date', 'Date')}</dt><dd>{formatDate(order.date)}</dd></div>
            <div className="summary-line"><dt>{t('confirmation.monastery', 'Monastery')}</dt><dd>{order.monastery.name}</dd></div>
            <div className="summary-line"><dt>{t('confirmation.location', 'Location')}</dt><dd>{order.monastery.location}</dd></div>
            <div className="summary-line"><dt>{t('confirmation.ritual', 'Ritual')}</dt><dd>{order.service.name}</dd></div>
            <div className="summary-line"><dt>{t('confirmation.offering', 'Offering')}</dt><dd>{order.optionLabel}</dd></div>
          </dl>
          {order.extraSupportCents > 0 && (
            <dl className="summary-breakdown">
              <div className="summary-line">
                <dt>{t('confirmation.offering', 'Offering')}</dt>
                <dd>{formatAmount(order.offeringAmountCents)}</dd>
              </div>
              <div className="summary-line">
                <dt>{t('checkout.extraSupportLine', 'Extra support')}</dt>
                <dd>{formatAmount(order.extraSupportCents)}</dd>
              </div>
            </dl>
          )}
          <div className="summary-total">
            <span className="muted" style={{ fontSize: 13 }}>{t('confirmation.contribution', 'Contribution')}</span>
            <b>{formatAmount(order.amountCents)}</b>
          </div>
          <FeeSplit
            offeringCents={order.offeringAmountCents}
            feeCents={order.platformFeeCents}
            monasteryName={order.monastery.name}
          />
        </section>

        <div className="row" style={{ justifyContent: 'center' }}>
          <Link to="/offerings" className="btn btn--primary">
            <Icon name="flame" size={17} solid />
            {t('confirmation.seeAllOfferings', 'See all my offerings')}
          </Link>
          <Link to="/explore" className="btn btn--ghost">{t('confirmation.makeAnother', 'Make another offering')}</Link>
        </div>
      </div>
    </div>
  );
}
